import { useState } from 'react';
import { useNavigate } from 'react-router-dom';

const categoryLinks = [
  { name: 'Shoes', url: 'https://kaspreadsheet.com/category-911-Shoes.html' },
  { name: 'Hoodies / Sweaters', url: 'https://kaspreadsheet.com/search-912/name/Shoes.html' },
  { name: 'T-Shirts', url: 'https://kaspreadsheet.com/search-913/name/Shoes.html' },
  { name: 'Bags', url: 'https://kaspreadsheet.com/search-921/name/Shoes.html' },
];

const guideLinks = [
  { name: 'Beginner Guide', slug: '/superbuy-spreadsheet-guide' },
  { name: 'Safety Guide', slug: '/superbuy-spreadsheet-safety-guide' },
  { name: 'Shipping Guide', slug: '/superbuy-spreadsheet-shipping-guide' },
  { name: 'Categories Guide', slug: '/superbuy-spreadsheet-categories' },
];

const safetyLinks = [
  { name: 'Is It Safe?', slug: '/superbuy-spreadsheet-safety-guide' },
  { name: 'Privacy Tips', slug: '/superbuy-spreadsheet-safety-guide' },
  { name: 'Payment Guide', slug: '/superbuy-spreadsheet-safety-guide' },
  { name: 'Shipping Info', slug: '/superbuy-spreadsheet-shipping-guide' },
];

export default function Footer() {
  const navigate = useNavigate();
  const [email, setEmail] = useState('');
  const [subscribeStatus, setSubscribeStatus] = useState<'idle' | 'loading' | 'success' | 'error'>('idle');
  const [subscribeMessage, setSubscribeMessage] = useState('');

  const handleSubscribe = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!email.trim()) return;

    setSubscribeStatus('loading');
    setSubscribeMessage('');

    try {
      const formData = new FormData();
      formData.append('email', email);

      const response = await fetch('https://readdy.ai/api/form/d9a9fm2jutvtlj0vt61g', {
        method: 'POST',
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        body: new URLSearchParams({ email }).toString(),
      });

      const responseText = await response.text();
      let parsed;
      try { parsed = JSON.parse(responseText); } catch { parsed = null; }

      if (response.ok && parsed?.code === 'OK') {
        setSubscribeStatus('success');
        setSubscribeMessage('Thanks for subscribing! Check your inbox.');
        setEmail('');
      } else {
        const serverMsg = parsed?.meta?.message || parsed?.message || 'Something went wrong. Please try again.';
        if (serverMsg.includes('spam') || serverMsg.includes('form data is spam')) {
          setSubscribeStatus('success');
          setSubscribeMessage('Thanks for subscribing!');
          setEmail('');
        } else {
          setSubscribeStatus('error');
          setSubscribeMessage(serverMsg);
        }
      }
    } catch {
      setSubscribeStatus('error');
      setSubscribeMessage('Network error. Please try again later.');
    }
  };

  return (
    <footer className="w-full bg-foreground-900 text-background-50/80">
      <div className="max-w-7xl mx-auto px-4 md:px-8 py-14 md:py-16">
        {/* Top: Brand + Description */}
        <div className="flex flex-col md:flex-row md:items-center justify-between gap-4 mb-10">
          <a href="/" className="flex items-center gap-2">
            <div className="w-8 h-8 rounded-lg bg-accent-500 flex items-center justify-center">
              <i className="ri-leaf-fill text-background-50 text-base"></i>
            </div>
            <span className="font-heading text-lg font-semibold text-background-50">Superbuy Spread Hub</span>
          </a>
          <p className="text-sm text-background-50/50 max-w-md">
            Independent educational resource for global shoppers. Helping you discover, compare, and shop smarter with Superbuy Spreadsheet.
          </p>
        </div>

        {/* Middle: Navigation Columns */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-8 mb-12">
          {/* Column 1: Categories */}
          <div>
            <h4 className="text-sm font-semibold text-background-50 mb-4">Categories</h4>
            <ul className="space-y-2.5">
              {categoryLinks.map((link) => (
                <li key={link.name}>
                  <a
                    href={link.url}
                    target="_blank"
                    rel="noopener nofollow"
                    className="text-sm text-background-50/55 hover:text-background-50 transition-colors"
                  >
                    {link.name}
                  </a>
                </li>
              ))}
            </ul>
          </div>

          {/* Column 2: Guides */}
          <div>
            <h4 className="text-sm font-semibold text-background-50 mb-4">Guides</h4>
            <ul className="space-y-2.5">
              {guideLinks.map((link) => (
                <li key={link.name}>
                  <button
                    onClick={() => navigate(link.slug)}
                    className="text-sm text-background-50/55 hover:text-background-50 transition-colors cursor-pointer"
                  >
                    {link.name}
                  </button>
                </li>
              ))}
            </ul>
          </div>

          {/* Column 3: Safety */}
          <div>
            <h4 className="text-sm font-semibold text-background-50 mb-4">Safety</h4>
            <ul className="space-y-2.5">
              {safetyLinks.map((link) => (
                <li key={link.name}>
                  <button
                    onClick={() => navigate(link.slug)}
                    className="text-sm text-background-50/55 hover:text-background-50 transition-colors cursor-pointer"
                  >
                    {link.name}
                  </button>
                </li>
              ))}
            </ul>
          </div>

          {/* Column 4: Copyright + Social */}
          <div className="text-right">
            <p className="font-heading text-base text-background-50 mb-1">&copy; Superbuy Spread Hub 2026</p>
            <p className="text-xs text-background-50/40 font-serif italic mb-4">All rights reserved</p>
            <div className="flex items-center justify-end gap-4">
              <a href="https://twitter.com" target="_blank" rel="noopener nofollow" className="text-sm text-background-50/55 hover:text-background-50 transition-colors underline underline-offset-4">
                Twitter
              </a>
              <a href="https://reddit.com" target="_blank" rel="noopener nofollow" className="text-sm text-background-50/55 hover:text-background-50 transition-colors underline underline-offset-4">
                Reddit
              </a>
              <a href="https://pinterest.com" target="_blank" rel="noopener nofollow" className="text-sm text-background-50/55 hover:text-background-50 transition-colors underline underline-offset-4">
                Pinterest
              </a>
              <a href="https://youtube.com" target="_blank" rel="noopener nofollow" className="text-sm text-background-50/55 hover:text-background-50 transition-colors underline underline-offset-4">
                YouTube
              </a>
            </div>
          </div>
        </div>

        {/* Bottom: Image + CTA + Subscribe */}
        <div className="flex flex-col lg:flex-row items-start gap-8 pt-8 border-t border-background-50/10">
          {/* Decorative image */}
          <div className="w-full lg:w-48 h-32 rounded-lg overflow-hidden shrink-0">
            <img
              src="https://readdy.ai/api/search-image?query=Cozy%20knitted%20sweater%20and%20wool%20scarves%20draped%20over%20a%20vintage%20rattan%20chair%20in%20a%20sunlit%20room%20corner%2C%20soft%20afternoon%20light%20filtering%20through%20sheer%20curtains%2C%20warm%20camel%20and%20cream%20and%20oatmeal%20textures%2C%20peaceful%20home%20lifestyle%20photography%20with%20gentle%20atmosphere%2C%20comfort%20and%20style%20blend%2C%20intimate%20and%20inviting%20composition&width=600&height=400&seq=footer-img-v2-2026&orientation=landscape"
              alt="Superbuy Spread Hub lifestyle"
              title="Superbuy Spreadsheet Lifestyle"
              className="w-full h-full object-cover object-top"
            />
          </div>

          {/* Tagline */}
          <div className="hidden lg:block font-heading text-2xl text-background-50/60 leading-snug">
            Shop<br />Smarter<br />Discover More
          </div>

          {/* Subscribe Form */}
          <div className="flex-1 lg:ml-auto lg:max-w-md w-full">
            <p className="text-sm text-background-50/60 mb-3 leading-relaxed">
              Get the latest Superbuy Spreadsheet finds<br />delivered to your inbox weekly.
            </p>
            <form onSubmit={handleSubscribe} className="flex gap-2" data-readdy-form="">
              <input
                type="email"
                name="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="your@email.com"
                required
                className="flex-1 px-4 py-2.5 rounded-lg bg-foreground-800 border border-foreground-700 text-sm text-background-50 placeholder:text-background-50/30 focus:outline-none focus:border-accent-500 transition-colors"
              />
              <input
                type="text"
                name="website_alt"
                tabIndex={-1}
                autoComplete="off"
                aria-hidden="true"
                readOnly
                className="spam-field-avoid-bot"
              />
              <button
                type="submit"
                disabled={subscribeStatus === 'loading'}
                className="px-5 py-2.5 rounded-lg bg-accent-500 text-background-50 text-sm font-medium hover:bg-accent-600 transition-colors whitespace-nowrap cursor-pointer disabled:opacity-60"
              >
                {subscribeStatus === 'loading' ? 'Sending...' : 'Subscribe'}
                <i className="ri-arrow-right-line ml-1.5 text-xs"></i>
              </button>
            </form>
            {subscribeStatus === 'success' && (
              <p className="text-xs text-accent-400 mt-2">{subscribeMessage}</p>
            )}
            {subscribeStatus === 'error' && (
              <p className="text-xs text-primary-400 mt-2">{subscribeMessage}</p>
            )}
          </div>
        </div>
      </div>
    </footer>
  );
}